import React from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import { tokens } from "../../../../../theme";
import { SettingsHeader, SettingsLayout } from "../../../../../component/settings";

import { pressable } from "../../../../../component/pressableProps";
// Settings hub — a single coherent home for configuration. Operational screens
// stay in their modules; this groups the org-wide + per-module *config* and links
// to where each lives (no module code is moved — just consistent navigation).
const GROUPS = [
  {
    title: "Work",
    description: "Company-wide working setup.",
    items: [
      { name: "Holidays", description: "Holiday calendar by legal entity.", path: "/app/settings/holidays" },
    ],
  },
  {
    title: "People & Access",
    description: "Org structure and who can do what.",
    items: [
      { name: "Legal Entities", description: "Registered company entities.", path: "/app/organization" },
      { name: "Departments", description: "Departments across the org.", path: "/app/organization/departments" },
      { name: "Designations", description: "Job titles and geofence defaults.", path: "/app/organization/designations" },
      { name: "Grades & Bands", description: "Leveling ladder and salary bands.", path: "/app/settings/grades" },
      { name: "Roles & Permissions", description: "Access control for users.", path: "/app/organization/roles" },
      { name: "Structure", description: "Departments, teams and headcount.", path: "/app/organization/org-chart" },
    ],
  },
  {
    title: "Attendance",
    description: "Schedules and attendance rules.",
    items: [
      { name: "Attendance Policies", description: "Grace, geofencing, penalties, regularization.", path: "/app/attendance-policy/listing" },
      { name: "Shifts", description: "Working schedules and day definitions.", path: "/app/shift/listing" },
      { name: "Shift Rotations", description: "Rotating shift rosters.", path: "/app/shift-rotation/listing" },
      { name: "Geofence", description: "Office boundaries for clock-in.", path: "/app/settings/geofence" },
    ],
  },
  {
    title: "Leave",
    description: "Leave entitlements and rules.",
    items: [
      { name: "Leave Policy", description: "Accrual and approval rules.", path: "/app/leave-policy" },
      { name: "Leave Types", description: "Paid, unpaid, and special leave.", path: "/app/leave-type" },
      { name: "Leave Encashment", description: "Cash-out of unused paid leave.", path: "/app/leave-encashment" },
    ],
  },
  {
    title: "Payroll",
    description: "Compensation configuration.",
    items: [
      { name: "Salary Structures", description: "Per-employee CTC breakdown.", path: "/app/settings/payroll/structures" },
      { name: "Salary Components", description: "Earnings and deductions catalog.", path: "/app/settings/payroll/components" },
      { name: "Statutory Components", description: "EPF, ESI, PT and custom statutory.", path: "/app/settings/payroll/statutory" },
    ],
  },
  {
    title: "Expenses",
    description: "Reimbursement and travel setup.",
    items: [
      { name: "Expense Policy", description: "Limits and approval rules.", path: "/app/expense-policy/listing" },
      { name: "Expense Categories", description: "Claimable expense types.", path: "/app/expense-type" },
      { name: "Trips Policy", description: "Travel rules and mileage.", path: "/app/trips/listing" },
      { name: "Vehicle Types", description: "Mileage rates by vehicle.", path: "/app/vehicle-type" },
    ],
  },
  {
    title: "Time",
    description: "Timesheet configuration.",
    items: [
      { name: "Timesheet Settings", description: "Projects, clients and rates.", path: "/app/timesheet-settings" },
    ],
  },
];

/**
 * One destination. A row rather than a card: the book gives a settings page one
 * column, and a wrapping grid of cards makes the eye scan in two directions to
 * find a name it already knows.
 */
const SettingRow = ({ item, onClick }) => (
  <Box
    {...pressable(onClick, item.name)}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "0.75rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      padding: "14px 16px",
      cursor: "pointer",
      "&:hover": { borderColor: tokens.borderStrong, background: tokens.surfaceSubtle },
    }}
  >
    <Box>
      <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>{item.name}</Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "3px", lineHeight: 1.5 }}>
        {item.description}
      </Box>
    </Box>
    <ChevronRightIcon sx={{ color: tokens.textMuted, fontSize: "1.25rem", flexShrink: 0 }} />
  </Box>
);

const Settings = () => {
  const navigate = useNavigate();
  const [active, setActive] = React.useState(GROUPS[0].title);
  const group = GROUPS.find((g) => g.title === active) || GROUPS[0];

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Settings"} />
      <MainBox sx={{ padding: "1.75rem" }}>
        <SettingsHeader
          title="Settings"
          subtitle="Configuration for the whole organisation. Changes apply from the next run of whatever they govern."
        />
        <SettingsLayout
          groups={GROUPS.map((g) => ({ key: g.title, label: g.title }))}
          active={active}
          onSelect={setActive}
        >
          <Box sx={{ fontSize: "1.1875rem", fontWeight: 700, color: tokens.text, mb: "6px" }}>
            {group.title}
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, lineHeight: 1.6, mb: "28px" }}>
            {group.description}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {group.items.map((item) => (
              <SettingRow key={item.name} item={item} onClick={() => navigate(item.path)} />
            ))}
          </Box>
        </SettingsLayout>
      </MainBox>
    </Box>
  );
};

export default Settings;
